/**
 * Login — 1:1 nach maestro_login/code.html (Stitch): edle Karte auf warmem,
 * leicht verschwommenem Hintergrund, eigenes Formular (E-Mail/Passwort mit Icons,
 * "Anmelden" mit Pfeil, Divider, "Mit Google anmelden", "Konto erstellen").
 * Echte Auth via Stack (signInWithCredential / signInWithOAuth); Sign-up -> /handler/sign-up (nur mit Einladung).
 */
import { useEffect, useState, type FormEvent } from "react";
import { stackApp } from "../stack";
import { Icon, Logo, Button } from "../ui/components";

/** Entscheidung 08.10.2026 „Nur mit Einladung": Registrieren nur, wenn das Ziel nach der Anmeldung
 *  die Einladungsannahme `/einladung?token=…` ist (so leitet /einladung Nicht-Angemeldete her). */
export function hatEinladungskontext(search: string): boolean {
  const ziel = new URLSearchParams(search).get("after_auth_return_to");
  if (!ziel) return false;
  try {
    const url = new URL(ziel, window.location.origin);
    return url.pathname === "/einladung" && !!url.searchParams.get("token");
  } catch {
    return false;
  }
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Entscheidung 2 (26.09.2026): nur die im Stack-Projekt eingeschalteten Wege zeigen. GitHub
  // bietet die Seite bewusst nie an (Entwickler-Anmeldung, nichts für Betriebe) — Google nur, wenn
  // es im Projekt aktiv ist.
  const [mitGoogle, setMitGoogle] = useState(false);
  useEffect(() => {
    let aktiv = true;
    stackApp
      .getProject()
      .then((p) => aktiv && setMitGoogle(p.config.oauthProviders.some((o) => o.id === "google")))
      .catch(() => undefined);
    return () => {
      aktiv = false;
    };
  }, []);
  // Seit /handler/sign-in hierher umleitet, kommt `after_auth_return_to` (z. B. die Einladung)
  // hier an — die Links zu Stack-Seiten reichen ihn weiter, sonst ginge das Ziel verloren.
  const weiter = window.location.search;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await stackApp.signInWithCredential({ email, password });
      if (res && typeof res === "object" && "status" in res && res.status === "error") {
        setError("E-Mail oder Passwort ist nicht korrekt.");
        setBusy(false);
        return;
      }
      window.location.href = "/";
    } catch (err) {
      // Reale Fehlermeldung zeigen (nicht verschlucken/erfinden).
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background font-body-md text-on-surface">
      {/* I4: dunkles Gradient statt hellem Blur (Design-Vorlage 2a) */}
      <img src="/img/login-v2.webp" alt="" className="absolute inset-0 z-0 h-full w-full object-cover" />
      <div className="absolute inset-0 z-0 bg-[linear-gradient(165deg,rgba(24,19,13,.55),rgba(24,19,13,.78))]" />

      <div className="relative z-10 w-full max-w-md px-margin-mobile md:px-0">
        <div className="overflow-hidden rounded-xl bg-surface-container-lowest shadow-[0_24px_60px_-12px_rgba(0,0,0,.55)]">
          <div className="flex flex-col items-center p-8 md:p-10">
            {/* I5: Newsreader-"M" Accent-Quadrat + Wortmarke statt Material-Icon */}
            <div className="mb-8 flex items-center gap-2.5">
              <Logo
                size={30}
                radiusClassName="rounded-[9px]"
                textClassName="text-[17px]"
                mitWortmarke
                wortmarkeClassName="font-body-sm text-[14px] font-bold tracking-[0.22em] text-on-background dark:text-inverse-on-surface"
              />
            </div>
            {/* I6: Begruessung */}
            <div className="mb-7 text-center">
              <h1 className="font-headline-md text-[21px] font-bold tracking-[-0.01em] text-on-background dark:text-inverse-on-surface">
                Willkommen zurück
              </h1>
              <p className="mt-1 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
                Melde dich im Backoffice deines Betriebs an
              </p>
            </div>

            <form className="flex w-full flex-col gap-6" onSubmit={(e) => void handleSubmit(e)}>
              {error && (
                <div className="rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="font-label-md text-label-md text-on-surface-variant" htmlFor="email">
                  E-Mail
                </label>
                <div className="relative">
                  <Icon
                    name="mail"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50"
                  />
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.com"
                    className="w-full rounded-lg border border-outline-variant bg-surface py-3 pl-10 pr-4 font-body-md text-body-md text-on-surface outline-none transition-colors placeholder:text-on-surface-variant/40 dark:placeholder:text-on-surface-variant/80 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                  <label className="font-label-md text-label-md text-on-surface-variant" htmlFor="password">
                    Passwort
                  </label>
                  {/* I6: Passwort-vergessen-Link (echte Stack-Auth-Route) */}
                  <a href={`/handler/forgot-password${weiter}`} className="font-body-sm text-[11.5px] font-medium text-primary hover:underline">
                    Vergessen?
                  </a>
                </div>
                <div className="relative">
                  <Icon
                    name="lock"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50"
                  />
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-outline-variant bg-surface py-3 pl-10 pr-10 font-body-md text-body-md text-on-surface outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    aria-label="Passwort anzeigen"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50 transition-colors hover:text-on-surface"
                  >
                    <Icon name={showPw ? "visibility" : "visibility_off"} />
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="mt-2 w-full !py-4"
              >
                {busy ? "Wird angemeldet …" : "Anmelden"}
                {!busy && <Icon name="arrow_forward" className="text-[18px]" />}
              </Button>
            </form>

            {mitGoogle && (
              <>
            <div className="my-8 flex w-full items-center gap-4">
              <div className="h-px flex-1 bg-outline-variant/50" />
              <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">
                oder
              </span>
              <div className="h-px flex-1 bg-outline-variant/50" />
            </div>

            <button
              type="button"
              onClick={() => void stackApp.signInWithOAuth("google")}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-outline-variant bg-surface-container-lowest py-3.5 font-label-md text-label-md text-on-surface transition-colors hover:bg-surface-container-low"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Mit Google anmelden
            </button>
              </>
            )}

            {hatEinladungskontext(weiter) && (
            <p className="mt-8 font-body-sm text-body-sm text-on-surface-variant">
              Neu hier?{" "}
              <a
                href={`/handler/sign-up${weiter}`}
                className="font-medium text-primary underline-offset-4 transition-colors hover:text-primary-container hover:underline"
              >
                Konto erstellen
              </a>
            </p>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-6 font-label-md text-label-md text-on-surface-variant/70">
          <span>powered by MAESTRO</span>
        </div>
      </div>
    </div>
  );
}
